// A two-person WebRTC call. Video and audio go directly between your browsers. The server
// only passes the setup messages and never handles the media.
//
// One side always calls and the other always answers, chosen by player id. "Perfect negotiation"
// (either side may offer) hung in 2 of 8 tests here when both offered at once, and with exactly
// two people there's no need for it. If the call isn't up within a few seconds, the caller redials.

export type CallStatus = "idle" | "waiting" | "connecting" | "connected" | "failed";

export interface CallOptions {
  /** True for exactly one of the two players. */
  caller: boolean;
  iceServers: RTCIceServer[];
  sendSignal: (data: unknown) => void;
  onRemote: (stream: MediaStream | null) => void;
  onStatus: (s: CallStatus) => void;
}

interface Signal { gen: number; description?: RTCSessionDescriptionInit; candidate?: RTCIceCandidateInit }

const REDIAL_MS = [6000, 8000, 12000, 20000];

export class Call {
  private pc: RTCPeerConnection | null = null;
  private gen = 0;
  private queued: RTCIceCandidateInit[] = [];
  private remoteSet = false;
  private watchdog: ReturnType<typeof setTimeout> | null = null;
  private attempts = 0;
  private peerHere = false;
  private closed = false;

  constructor(private local: MediaStream, private o: CallOptions) {
    o.onStatus("waiting");
  }

  private build(gen: number): RTCPeerConnection {
    this.teardownPc();
    this.gen = gen;
    this.remoteSet = false;
    this.queued = [];
    const pc = new RTCPeerConnection({ iceServers: this.o.iceServers });
    this.pc = pc;
    for (const track of this.local.getTracks()) pc.addTrack(track, this.local);
    pc.onicecandidate = (e) => { if (e.candidate && this.pc === pc) this.o.sendSignal({ gen, candidate: e.candidate.toJSON() } satisfies Signal); };
    pc.ontrack = (e) => {
      if (this.pc !== pc) return;
      this.o.onRemote(e.streams[0] ?? new MediaStream([e.track]));
    };
    pc.onconnectionstatechange = () => {
      if (this.pc !== pc) return;
      const s = pc.connectionState;
      if (s === "connected") {
        this.attempts = 0;
        this.clearWatchdog();
        this.o.onStatus("connected");
      } else if (s === "failed") {
        this.o.onStatus("failed");
        if (this.o.caller) this.dial();
      } else if (s === "disconnected") {
        this.o.onStatus("connecting");
        // Often recovers by itself after a network blip. If not, redial.
        if (this.o.caller) this.armWatchdog();
      }
    };
    return pc;
  }

  private async dial(): Promise<void> {
    if (this.closed || !this.peerHere || !this.o.caller) return;
    const gen = Date.now();
    const pc = this.build(gen);
    this.o.onStatus("connecting");
    this.armWatchdog();
    try {
      await pc.setLocalDescription(await pc.createOffer());
      if (this.pc === pc) this.o.sendSignal({ gen, description: pc.localDescription!.toJSON() } satisfies Signal);
    } catch (e) {
      console.warn("offer failed", e);
    }
  }

  private armWatchdog(): void {
    this.clearWatchdog();
    const wait = REDIAL_MS[Math.min(this.attempts, REDIAL_MS.length - 1)]!;
    this.attempts++;
    this.watchdog = setTimeout(() => {
      if (this.pc?.connectionState !== "connected") {
        // After several tries, say so: the person can fall back to FaceTime.
        if (this.attempts >= 3) this.o.onStatus("failed");
        void this.dial();
      }
    }, wait);
  }

  private clearWatchdog(): void {
    if (this.watchdog) { clearTimeout(this.watchdog); this.watchdog = null; }
  }

  peerPresent(present: boolean): void {
    this.peerHere = present;
    if (present) {
      this.attempts = 0;
      if (this.o.caller) void this.dial();
      else this.o.onStatus("connecting");
      return;
    }
    this.clearWatchdog();
    this.teardownPc();
    this.o.onRemote(null);
    this.o.onStatus("waiting");
  }

  async signal(raw: unknown): Promise<void> {
    const data = raw as Signal;
    if (this.closed) return;
    try {
      if (data.description?.type === "offer") {
        // Answerer: a new offer always wins, since it means the caller redialed.
        if (this.o.caller || data.gen < this.gen) return;
        const pc = this.build(data.gen);
        this.o.onStatus("connecting");
        await pc.setRemoteDescription(data.description);
        await this.flush(pc);
        await pc.setLocalDescription(await pc.createAnswer());
        if (this.pc === pc) this.o.sendSignal({ gen: data.gen, description: pc.localDescription!.toJSON() } satisfies Signal);
      } else if (data.description?.type === "answer") {
        if (!this.o.caller || data.gen !== this.gen || !this.pc) return;
        await this.pc.setRemoteDescription(data.description);
        await this.flush(this.pc);
      } else if (data.candidate) {
        if (data.gen !== this.gen || !this.pc) return;
        if (this.remoteSet) await this.pc.addIceCandidate(data.candidate);
        else this.queued.push(data.candidate);
      }
    } catch (e) {
      console.warn("signal failed", e);
    }
  }

  private async flush(pc: RTCPeerConnection): Promise<void> {
    this.remoteSet = true;
    const q = this.queued;
    this.queued = [];
    for (const c of q) await pc.addIceCandidate(c).catch((e) => console.warn("candidate", e));
  }

  private teardownPc(): void {
    if (this.pc) {
      this.pc.onicecandidate = null;
      this.pc.ontrack = null;
      this.pc.onconnectionstatechange = null;
      this.pc.close();
      this.pc = null;
    }
  }

  close(): void {
    this.closed = true;
    this.clearWatchdog();
    this.teardownPc();
  }
}
